/**
 * Percent annotation spec -> SVG primitives, in image-pixel user units.
 *
 * This module is the console half of the NORMATIVE GEOMETRY CONTRACT documented
 * in `agentic_workflow/utils/annotation_renderer.py`. That docstring is the
 * source of truth; this file exists to reproduce it so the live preview matches
 * the baked PNG. If the two ever disagree, the renderer is right and this file
 * is wrong — except where amending both together is the deliberate fix.
 *
 * Output is expressed in **1x image pixels** on a `viewBox="0 0 W H"`, because
 * `px = x_pct / 100 * W` is non-uniform (CSS-like) and needs no adaptation.
 *
 * The six rules that are easy to get wrong, and where each is implemented:
 *
 *  1. Pillow strokes rect/ellipse INWARD from the box edge; SVG strokes CENTRED.
 *     -> inset by `strokePx / 2` on all sides (`rectPrimitive`, `ellipsePrimitive`).
 *  2. Pillow's `joint="curve"` rounds interior joints but leaves butt caps; the
 *     renderer stamps a filled circle at each polyline end.
 *     -> `linecap: 'round'` + `linejoin: 'round'` (`strokePrimitive`).
 *  3. Text is anchored `"la"` (left / ascender).
 *     -> `text-anchor="start"`, `dominant-baseline="hanging"`
 *        (consumed in `annotationSvg.ts`).
 *  4. Effective stroke width is QUANTISED BY THE SUPERSAMPLE. The renderer draws
 *     at `ss x` with `max(1, round(strokePx1x * ss))` and LANCZOS-downsamples, so
 *     the effective 1x width is `round(strokePx1x * ss) / ss`, not `strokePx1x`.
 *     At `ss == 1` (sources over 8 MPx) a 3.7px stroke bakes as 4px.
 *     -> `effectiveStrokePx`.
 *  5. Font size is integer-quantised the same way -> `effectiveFontPx`.
 *  6. The arrow shaft terminates at the head BASE, not the tip, and vanishes
 *     entirely when the gesture is shorter than the head. -> `arrowPrimitives`.
 *
 * Pure: no React, no DOM, no measurement. Everything here is unit-testable and
 * is pinned by `__tests__/annotationGeometry.test.ts`.
 */

import type { AnnotationPointDict, ImageAnnotationDict } from '../../../../../generated/nodeDataTypes'
export { ANNOTATION_FONT_FAMILY, ANNOTATION_FONT_WEIGHT } from './annotationFont'

/**
 * The kinds the renderer understands. Mirrors `_POINT_CARDINALITY` in
 * `annotation_renderer.py`. The generated `ImageAnnotationDict.kind` is a bare
 * `string`, so this alias is how we narrow without a forbidden cast.
 */
export type AnnotationKind = 'stroke' | 'rect' | 'ellipse' | 'arrow' | 'text'

export const ANNOTATION_KINDS: readonly AnnotationKind[] = [
  'stroke',
  'rect',
  'ellipse',
  'arrow',
  'text',
]

/**
 * Mirrors `_POINT_CARDINALITY` in `annotation_renderer.py`. The renderer
 * validates the whole array before drawing anything; the console must too, or a
 * malformed spec surfaces as `Cannot read properties of undefined` from inside a
 * primitive builder — opaque, and thrown during render rather than at the point
 * the bad element entered.
 */
const POINT_CARDINALITY: Readonly<Record<AnnotationKind, { min: number; max: number }>> = {
  stroke: { min: 2, max: Number.POSITIVE_INFINITY },
  rect: { min: 2, max: 2 },
  ellipse: { min: 2, max: 2 },
  arrow: { min: 2, max: 2 },
  text: { min: 1, max: 1 },
}

/** Mirrors `MAX_ANNOTATIONS` in `annotation_renderer.py` and `max_length` on the route model. */
export const MAX_ANNOTATIONS = 200

/** Mirrors `_ARROW_HEAD_LEN_RATIO` / `_ARROW_HEAD_HALF_WIDTH_RATIO`. */
const ARROW_HEAD_LEN_RATIO = 4
const ARROW_HEAD_HALF_WIDTH_RATIO = 2

/** Mirrors `_SUPERSAMPLE_LADDER` — (max pixel count, factor), first match wins. */
const SUPERSAMPLE_LADDER: readonly (readonly [number, number])[] = [
  [2_000_000, 4],
  [8_000_000, 2],
]

export interface Vec2 {
  x: number
  y: number
}

export interface PolylinePrimitive {
  shape: 'polyline'
  id: string
  points: Vec2[]
  color: string
  strokeWidth: number
}

export interface RectPrimitive {
  shape: 'rect'
  id: string
  x: number
  y: number
  width: number
  height: number
  color: string
  /** `0` means "filled" — the degenerate collapse described in `rectPrimitive`. */
  strokeWidth: number
  filled: boolean
}

export interface EllipsePrimitive {
  shape: 'ellipse'
  id: string
  cx: number
  cy: number
  rx: number
  ry: number
  color: string
  strokeWidth: number
  filled: boolean
}

export interface PolygonPrimitive {
  shape: 'polygon'
  id: string
  points: Vec2[]
  color: string
}

export interface DotPrimitive {
  shape: 'dot'
  id: string
  cx: number
  cy: number
  r: number
  color: string
}

export interface TextPrimitive {
  shape: 'text'
  id: string
  x: number
  y: number
  text: string
  color: string
  fontSize: number
}

export type AnnotationPrimitive =
  | PolylinePrimitive
  | RectPrimitive
  | EllipsePrimitive
  | PolygonPrimitive
  | DotPrimitive
  | TextPrimitive

/** Mirrors `_pick_supersample`. */
export function pickSupersample(width: number, height: number): number {
  const pixels = width * height
  for (const [maxPixels, factor] of SUPERSAMPLE_LADDER) {
    if (pixels <= maxPixels) return factor
  }
  return 1
}

/**
 * Effective 1x stroke width, INCLUDING the supersample quantisation (rule 4).
 *
 * The renderer computes `max(1.0, pct/100 * min(W,H))` at 1x, multiplies by the
 * supersample, rounds to a whole device pixel, then downsamples. Skipping the
 * round-trip here is the single most likely source of a preview that looks right
 * and bakes half a pixel thinner.
 */
export function effectiveStrokePx(
  strokeWidthPct: number,
  width: number,
  height: number,
  supersample: number,
): number {
  const strokePx1x = Math.max(1, (strokeWidthPct / 100) * Math.min(width, height))
  return Math.max(1, Math.round(strokePx1x * supersample)) / supersample
}

/** Effective 1x font size. `* ss` then `/ ss` cancels, leaving the integer round. */
export function effectiveFontPx(fontSizePct: number, height: number): number {
  return Math.max(8, Math.round((fontSizePct / 100) * height))
}

/** Narrow the generated `string` kind without a cast. Returns null for unknown kinds. */
export function toAnnotationKind(kind: string): AnnotationKind | null {
  for (const candidate of ANNOTATION_KINDS) {
    if (candidate === kind) return candidate
  }
  return null
}

function toPixels(points: AnnotationPointDict[], width: number, height: number): Vec2[] {
  return points.map((point) => ({
    x: (point.x / 100) * width,
    y: (point.y / 100) * height,
  }))
}

/** Mirrors `_normalized_box` — any diagonal normalizes to (min, min, max, max). */
function normalizedBox(points: Vec2[]): { x0: number; y0: number; x1: number; y1: number } {
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  return {
    x0: Math.min(...xs),
    y0: Math.min(...ys),
    x1: Math.max(...xs),
    y1: Math.max(...ys),
  }
}

function strokePrimitive(
  id: string,
  points: Vec2[],
  color: string,
  strokeWidth: number,
): PolylinePrimitive {
  // Rule 2: round caps + joins. The renderer stamps an explicit filled circle at
  // each end because Pillow's `joint="curve"` leaves butt caps there; SVG gets
  // the same result from `stroke-linecap`.
  return { shape: 'polyline', id, points, color, strokeWidth }
}

function rectPrimitive(
  id: string,
  box: { x0: number; y0: number; x1: number; y1: number },
  color: string,
  strokeWidth: number,
): RectPrimitive {
  // Rule 1: Pillow strokes inward from the box edge, so the stroke centre-line
  // sits half a stroke inside it.
  const inset = strokeWidth / 2
  const width = box.x1 - box.x0 - strokeWidth
  const height = box.y1 - box.y0 - strokeWidth
  if (width <= 0 || height <= 0) {
    // The inset has collapsed the shape. Pillow's inward stroke fills such a box
    // solid; an SVG rect with a negative dimension is invalid and would vanish.
    // Fall back to a filled rect of the raw box, which is what the bake shows.
    return {
      shape: 'rect',
      id,
      x: box.x0,
      y: box.y0,
      width: Math.max(0, box.x1 - box.x0),
      height: Math.max(0, box.y1 - box.y0),
      color,
      strokeWidth: 0,
      filled: true,
    }
  }
  return {
    shape: 'rect',
    id,
    x: box.x0 + inset,
    y: box.y0 + inset,
    width,
    height,
    color,
    strokeWidth,
    filled: false,
  }
}

function ellipsePrimitive(
  id: string,
  box: { x0: number; y0: number; x1: number; y1: number },
  color: string,
  strokeWidth: number,
): EllipsePrimitive {
  // Rule 1, ellipse form: rx = |x1 - x0| / 2 - strokePx / 2.
  const cx = (box.x0 + box.x1) / 2
  const cy = (box.y0 + box.y1) / 2
  const rx = (box.x1 - box.x0) / 2 - strokeWidth / 2
  const ry = (box.y1 - box.y0) / 2 - strokeWidth / 2
  if (rx <= 0 || ry <= 0) {
    // Same collapse as `rectPrimitive`: render the raw box filled.
    return {
      shape: 'ellipse',
      id,
      cx,
      cy,
      rx: Math.max(0, (box.x1 - box.x0) / 2),
      ry: Math.max(0, (box.y1 - box.y0) / 2),
      color,
      strokeWidth: 0,
      filled: true,
    }
  }
  return { shape: 'ellipse', id, cx, cy, rx, ry, color, strokeWidth, filled: false }
}

/**
 * Mirrors `_draw_arrow` exactly, including both degenerate branches (rule 7).
 *
 * Emits up to two primitives: the shaft (omitted when the gesture is no longer
 * than the head) and the head triangle. A zero-length arrow is a single dot of
 * diameter `strokeWidth` — a truthful rendering of a zero-length gesture, not an
 * error.
 */
function arrowPrimitives(
  id: string,
  tail: Vec2,
  head: Vec2,
  color: string,
  strokeWidth: number,
): AnnotationPrimitive[] {
  const dx = head.x - tail.x
  const dy = head.y - tail.y
  const length = Math.hypot(dx, dy)

  if (length <= 0) {
    return [{ shape: 'dot', id, cx: head.x, cy: head.y, r: strokeWidth / 2, color }]
  }

  let headLen = ARROW_HEAD_LEN_RATIO * strokeWidth
  let headHalfWidth = ARROW_HEAD_HALF_WIDTH_RATIO * strokeWidth
  if (length < headLen) {
    // Degenerate: scale the head down so the whole arrow fits the gesture.
    headHalfWidth *= length / headLen
    headLen = length
  }

  const ux = dx / length
  const uy = dy / length
  const base: Vec2 = { x: head.x - headLen * ux, y: head.y - headLen * uy }

  const primitives: AnnotationPrimitive[] = []
  if (length > headLen) {
    primitives.push(strokePrimitive(`${id}:shaft`, [tail, base], color, strokeWidth))
  }
  // Perpendicular unit vector.
  const px = -uy
  const py = ux
  primitives.push({
    shape: 'polygon',
    id: `${id}:head`,
    points: [
      head,
      { x: base.x + headHalfWidth * px, y: base.y + headHalfWidth * py },
      { x: base.x - headHalfWidth * px, y: base.y - headHalfWidth * py },
    ],
    color,
  })
  return primitives
}

function textPrimitive(
  id: string,
  anchor: Vec2,
  text: string,
  color: string,
  fontSize: number,
): TextPrimitive {
  return {
    shape: 'text',
    id,
    x: anchor.x,
    y: anchor.y,
    text,
    color,
    fontSize,
  }
}

/**
 * Build the SVG primitives for one annotation spec against a source of
 * `width` x `height` pixels.
 *
 * Draw order is array order, first to last, matching both the renderer's loop
 * and SVG document order. Never sort by kind.
 *
 * Unknown kinds throw rather than being skipped — a silently dropped element is
 * a preview that disagrees with the bake, which is the exact class of bug this
 * module exists to prevent (Dev Principle 2).
 */
export function buildAnnotationPrimitives(
  annotations: ImageAnnotationDict[],
  width: number,
  height: number,
): AnnotationPrimitive[] {
  const supersample = pickSupersample(width, height)
  const primitives: AnnotationPrimitive[] = []

  for (const element of annotations) {
    const kind = toAnnotationKind(element.kind)
    if (kind === null) {
      throw new Error(
        `Unknown annotation kind ${JSON.stringify(element.kind)} on element ${element.id}. ` +
          `Expected one of: ${ANNOTATION_KINDS.join(', ')}.`,
      )
    }
    const cardinality = POINT_CARDINALITY[kind]
    const pointCount = element.points.length
    if (pointCount < cardinality.min || pointCount > cardinality.max) {
      const expected =
        cardinality.max === Number.POSITIVE_INFINITY
          ? `at least ${cardinality.min}`
          : `exactly ${cardinality.min}`
      throw new Error(
        `Annotation ${element.id} of kind ${kind} needs ${expected} point(s), got ${pointCount}.`,
      )
    }
    const points = toPixels(element.points, width, height)

    if (kind === 'text') {
      const fontSizePct = element.font_size
      if (fontSizePct === undefined) {
        throw new Error(`Text annotation ${element.id} is missing font_size.`)
      }
      const text = element.text
      if (text === undefined || text === '') {
        throw new Error(`Text annotation ${element.id} is missing text.`)
      }
      primitives.push(
        textPrimitive(
          element.id,
          points[0],
          text,
          element.color,
          effectiveFontPx(fontSizePct, height),
        ),
      )
      continue
    }

    const strokeWidth = effectiveStrokePx(element.stroke_width, width, height, supersample)

    switch (kind) {
      case 'stroke':
        primitives.push(strokePrimitive(element.id, points, element.color, strokeWidth))
        break
      case 'rect':
        primitives.push(
          rectPrimitive(element.id, normalizedBox(points), element.color, strokeWidth),
        )
        break
      case 'ellipse':
        primitives.push(
          ellipsePrimitive(element.id, normalizedBox(points), element.color, strokeWidth),
        )
        break
      case 'arrow':
        primitives.push(
          ...arrowPrimitives(element.id, points[0], points[1], element.color, strokeWidth),
        )
        break
    }
  }

  return primitives
}
